/**
 * FamilyTab — Barna-fanen på /foreldre (#159, redesign steg 9a, Stell_Familie).
 *
 * Barnekortene først, så raden «N medlemmer · Inviter / legg til». Raden
 * åpner medlemsadministrasjonen (MemberAdminSection) under seg, med invitasjon,
 * nytt passord, redigering og arkivering som før. Før sto hele listen alltid
 * åpen under barnekortene; skissen legger den bak en rad, fordi den brukes
 * sjelden.
 *
 * Raden vises når medlemslisten er hentet, og blir stående om en ny henting
 * feiler, så et åpent redigeringsskjema ikke forsvinner. Er adminopplåsingen
 * utløpt, viser barneseksjonen AdminScopeLocked, og raden venter til den er
 * låst opp.
 */
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../../components/primitives';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { InitialAvatarStack, asColorSlot } from '../../components/primitives/InitialAvatar';
import { MemberAdminSection } from '../admin/MemberAdminSection';
import { isAdminScopeError, useAdminMembers } from '../admin/useMembers';
import { ChildOverviewSection } from './ChildOverviewSection';

const MEMBERS_PANEL_ID = 'family-members-admin';

export function FamilyTab(): ReactElement {
  const { t } = useTranslation();
  const members = useAdminMembers();
  const [membersOpen, setMembersOpen] = useState(false);

  const active = (members.data ?? []).filter((m) => !m.archived_at);
  const scopeLocked = members.isError && isAdminScopeError(members.error);

  return (
    <div className="flex flex-col gap-3" data-testid="parent-console-family">
      <ChildOverviewSection />

      {members.data && !scopeLocked ? (
        <>
          <button
            type="button"
            aria-expanded={membersOpen}
            aria-controls={MEMBERS_PANEL_ID}
            onClick={() => setMembersOpen((v) => !v)}
            data-testid="family-members-row"
            className={`${GLASS_SURFACE} flex min-h-15 w-full items-center gap-3 rounded-lg px-3.5 py-2.5 text-left shadow-md`}
          >
            <InitialAvatarStack
              members={active.map((m) => ({ name: m.short_name, colorSlot: asColorSlot(m.color_token) }))}
              size={30}
            />
            <span className="min-w-0 flex-1 text-sm font-extrabold text-ink">
              {t('admin.members.count', { count: active.length })}
            </span>
            <span className="text-sm font-extrabold text-accent-text">{t('admin.members.manage')}</span>
          </button>
          {membersOpen ? (
            <div id={MEMBERS_PANEL_ID}>
              <Card className="p-5">
                <MemberAdminSection />
              </Card>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
